import type {
  CurrentAssetIntelligenceAsset,
  CurrentAssetIntelligenceBrowserSafeProjectionProps,
  CurrentAssetIntelligenceOpportunity,
  CurrentAssetIntelligencePreviewCard,
} from "../../lib/currentAssetIntelligence/browserSafeReadModel";
import { mapExistingSourceReadToBrowserSafeReadModel } from "../../lib/currentAssetIntelligence/existingSourceReadAdapter";
import { torenVerifiedExistingSourceSnapshot } from "../../lib/currentAssetIntelligence/fixtures/torenVerifiedExistingSourceSnapshot";
import { BROWSER_SAFE_INTERNAL_REVIEW_PACK_FIXTURE } from "../../lib/currentAssetIntelligence/browserSafeInternalReviewPackFixture";
import type { InternalReviewPackResult } from "../../lib/currentAssetIntelligence/browserSafeInternalReviewPackAdapter";

export type {
  CurrentAssetIntelligenceAsset,
  CurrentAssetIntelligenceAssetStatus,
  CurrentAssetIntelligenceBrowserSafeProjectionProps,
  CurrentAssetIntelligenceBrowserSafeProps,
  CurrentAssetIntelligenceOpportunity,
  CurrentAssetIntelligenceRegistryAsset,
  CurrentAssetIntelligencePreviewCard,
  CurrentAssetIntelligencePreviewStatus,
  CurrentAssetIntelligenceInternalDataSourceReadout,
} from "../../lib/currentAssetIntelligence/browserSafeReadModel";

type ValidatedInternalUiProjectionArtifact = {
  visibility?: string;
  routePath?: string;
  componentName?: string;
  projectionSections?: Record<string, boolean>;
  customerOutputDisabled?: boolean;
  browserSupabaseReads?: number;
  browserSupabaseWrites?: number;
  customerFacing?: boolean;
  customerRouteMounted?: boolean;
  customerAccessGranted?: boolean;
  customerReadModelExposed?: boolean;
  featureFlagWritten?: boolean;
  adminUiRendered?: boolean;
  aiCalls?: number;
  liveNetworkActions?: number;
  accountRunContext?: CurrentAssetIntelligenceBrowserSafeProjectionProps["accountRunContext"];
  readoutSummary?: CurrentAssetIntelligenceBrowserSafeProjectionProps["readoutSummary"];
  assetCoverage?: CurrentAssetIntelligencePreviewCard;
  entityClarity?: CurrentAssetIntelligencePreviewCard;
  aiExtractability?: CurrentAssetIntelligencePreviewCard;
  competitiveCompleteness?: CurrentAssetIntelligencePreviewCard;
  recommendedNextSteps?: string[];
  previewSummary?: CurrentAssetIntelligenceBrowserSafeProjectionProps["previewSummary"];
  assetInventory?: CurrentAssetIntelligenceAsset[];
  opportunityQueue?: CurrentAssetIntelligenceOpportunity[];
  sourceWarnings?: string[];
  sourceRowCounts?: Record<string, number>;
  scenario?: CurrentAssetIntelligenceBrowserSafeProjectionProps["scenario"];
  sourceMode?: string;
  runDate?: string;
  profileLabel?: string;
  scenarioLabel?: string;
  scenarioDetail?: string;
};

/**
 * Browser-safe fixture adapter. The server/CI result validator must run first;
 * this adapter intentionally performs no validation, reads, writes, or imports
 * from src/lib/agentSmith.
 */
export function mapValidatedInternalUiProjectionToBrowserSafeProps(
  artifact: ValidatedInternalUiProjectionArtifact
): CurrentAssetIntelligenceBrowserSafeProjectionProps {
  return {
    visibility: "internal_admin_browser_safe_route_fixture_props",
    routePath: "/admin/current-asset-intelligence",
    componentName: "CurrentAssetIntelligenceBrowserSafe",
    projectionStatus: "validated_internal_ui_projection",
    scenario: artifact.scenario || "demoReady",
    sourceMode: artifact.sourceMode,
    runDate: artifact.runDate,
    profileLabel: artifact.profileLabel || "Internal demo profile",
    scenarioLabel: artifact.scenarioLabel || "Demo-ready internal preview",
    scenarioDetail: artifact.scenarioDetail || "Static fixture data is available for internal review.",
    projectionSections: { ...(artifact.projectionSections || {}) },
    accountRunContext: artifact.accountRunContext || {
      accountLabel: "Internal demo account",
      accountId: "fixture-account",
      runId: "fixture-run",
      source: "Static internal fixture",
      dataMode: "Demo only",
    },
    readoutSummary: artifact.readoutSummary || {
      headline: "Prepared internal readout",
      detail: "Static projection data only; no live account data is connected.",
      status: "internal-only",
      scope: "Demo projection",
    },
    assetCoverage: artifact.assetCoverage || {
      label: "Asset coverage",
      value: "Fixture mapped",
      detail: "Demo asset fields are present for internal layout validation.",
      status: "internal-only",
    },
    entityClarity: artifact.entityClarity || {
      label: "Entity clarity",
      value: "Reviewable",
      detail: "Entity relationships are represented by static fixture metadata.",
      status: "review",
    },
    aiExtractability: artifact.aiExtractability || {
      label: "AI extractability",
      value: "Not executed",
      detail: "No model call or live assessment is enabled in this preview.",
      status: "blocked",
    },
    competitiveCompleteness: artifact.competitiveCompleteness || {
      label: "Competitive completeness",
      value: "Partial fixture",
      detail: "Competitive signals remain an internal follow-up area.",
      status: "review",
    },
    recommendedNextSteps: artifact.recommendedNextSteps || [
      "Review the static projection shape with the internal operator.",
      "Approve a scoped server-side read before connecting real account data.",
      "Re-run safety and customer-output gates before any rollout decision.",
    ],
    previewSummary: artifact.previewSummary || {
      totalAssetsReviewed: 0,
      assetsReadyForInternalReview: 0,
      blockedProductionActions: 0,
      customerFacingOutputs: 0,
      browserSupabaseReads: 0,
    },
    assetInventory: artifact.assetInventory || [],
    opportunityQueue: artifact.opportunityQueue || [],
    sourceWarnings: artifact.sourceWarnings || [],
    sourceRowCounts: artifact.sourceRowCounts || {},
    safetyLocks: {
      customerOutputDisabled: artifact.customerOutputDisabled === true,
      browserSupabaseReads: Number(artifact.browserSupabaseReads || 0),
      browserSupabaseWrites: Number(artifact.browserSupabaseWrites || 0),
      customerFacing: artifact.customerFacing === true,
      customerRouteMounted: artifact.customerRouteMounted === true,
      customerAccessGranted: artifact.customerAccessGranted === true,
      customerReadModelExposed: artifact.customerReadModelExposed === true,
      featureFlagWritten: artifact.featureFlagWritten === true,
      adminUiRendered: artifact.adminUiRendered === true,
      aiCalls: Number(artifact.aiCalls || 0),
      liveNetworkActions: Number(artifact.liveNetworkActions || 0),
    },
  };
}

export const adaptValidatedInternalUiProjectionToBrowserSafeProps =
  mapValidatedInternalUiProjectionToBrowserSafeProps;

const STATIC_INTERNAL_UI_PROJECTION_FIXTURE: ValidatedInternalUiProjectionArtifact = {
  visibility: "internal_admin_browser_safe_route_ui_projection_one_run_artifact",
  routePath: "/admin/current-asset-intelligence",
  componentName: "CurrentAssetIntelligenceBrowserSafe",
  projectionSections: {
    statusHeader: true,
    safetyCards: true,
    routeDiagnostics: true,
    browserSafetyLocks: true,
    customerExposureLocks: true,
    counterPanel: true,
    blockedActions: true,
    nextSteps: true,
    rollbackPanel: true,
    debugMetadata: true,
  },
  scenario: "demoReady",
  profileLabel: "Enterprise demo profile",
  scenarioLabel: "Demo-ready internal preview",
  scenarioDetail: "Static fixture data is available for internal review.",
  customerOutputDisabled: true,
  browserSupabaseReads: 0,
  browserSupabaseWrites: 0,
  customerFacing: false,
  customerRouteMounted: false,
  customerAccessGranted: false,
  customerReadModelExposed: false,
  featureFlagWritten: false,
  adminUiRendered: false,
  aiCalls: 0,
  liveNetworkActions: 0,
  accountRunContext: {
    accountLabel: "Northstar Systems · internal demo",
    accountId: "acct_internal_current_asset_demo",
    runId: "run_current_asset_browser_props_demo",
    source: "Static internal fixture",
    dataMode: "Demo only",
  },
  readoutSummary: {
    headline: "Prepared Current Asset readout",
    detail: "A validated internal projection is available for layout and operator review.",
    status: "internal-only",
    scope: "One demo account/run",
  },
  assetCoverage: {
    label: "Asset coverage",
    value: "8 of 10 demo signals",
    detail: "Core asset, page, and metadata fields are represented in the fixture.",
    status: "review",
  },
  entityClarity: {
    label: "Entity clarity",
    value: "Clear enough to review",
    detail: "Account, run, asset, and opportunity relationships are explicitly scoped.",
    status: "ready",
  },
  aiExtractability: {
    label: "AI extractability",
    value: "Gated · not executed",
    detail: "The fixture describes the future shape without making an AI call.",
    status: "blocked",
  },
  competitiveCompleteness: {
    label: "Competitive completeness",
    value: "6 of 10 demo signals",
    detail: "Competitive context is intentionally partial and marked for internal follow-up.",
    status: "review",
  },
  recommendedNextSteps: [
    "Review coverage gaps and entity links with the internal operator.",
    "Approve one scoped server-side read path before introducing real CAI data.",
    "Keep AI assessment, customer output, and rollout actions behind their existing gates.",
  ],
  previewSummary: {
    totalAssetsReviewed: 5,
    assetsReadyForInternalReview: 3,
    blockedProductionActions: 6,
    customerFacingOutputs: 0,
    browserSupabaseReads: 0,
  },
  assetInventory: [
    {
      assetId: "asset-homepage-001",
      kind: "Homepage",
      title: "Northstar Systems homepage",
      path: "/",
      lastReviewed: "2026-07-28",
      statuses: {
        assetCoverage: { status: "ready", value: "Complete", detail: "Primary page structure and core metadata are represented." },
        entityClarity: { status: "ready", value: "Clear", detail: "Organization and primary solution entity are linked." },
        aiExtractability: { status: "review", value: "Review", detail: "Static fixture includes copy blocks but no model extraction." },
        competitiveCompleteness: { status: "review", value: "Partial", detail: "Two competitive claims are represented for operator review." },
        freshnessTrust: { status: "ready", value: "Trusted fixture", detail: "Reviewed in the static demo run; not a live freshness signal." },
      },
    },
    {
      assetId: "asset-services-002",
      kind: "Category / service",
      title: "Enterprise access services",
      path: "/solutions/enterprise-access",
      lastReviewed: "2026-07-27",
      statuses: {
        assetCoverage: { status: "ready", value: "Complete", detail: "Service taxonomy, navigation, and conversion path are present." },
        entityClarity: { status: "ready", value: "Clear", detail: "Service category is associated with the account and solution family." },
        aiExtractability: { status: "review", value: "Review", detail: "Long-form service proof is marked for future extraction." },
        competitiveCompleteness: { status: "review", value: "Needs proof", detail: "Competitor comparison evidence is incomplete in the fixture." },
        freshnessTrust: { status: "review", value: "Watch", detail: "Static review date is older than the demo homepage fixture." },
      },
    },
    {
      assetId: "asset-product-003",
      kind: "Product / solution",
      title: "Access control platform",
      path: "/products/access-control-platform",
      lastReviewed: "2026-07-29",
      statuses: {
        assetCoverage: { status: "ready", value: "Complete", detail: "Product positioning, feature groups, and CTA are mapped." },
        entityClarity: { status: "ready", value: "Clear", detail: "Product entity and supporting capabilities are unambiguous." },
        aiExtractability: { status: "blocked", value: "Gated", detail: "No AI assessment is permitted in this browser-safe preview." },
        competitiveCompleteness: { status: "ready", value: "Strong", detail: "Static competitor and differentiation fields are present." },
        freshnessTrust: { status: "ready", value: "Trusted fixture", detail: "Included as the newest example in the demo run." },
      },
    },
    {
      assetId: "asset-pdf-004",
      kind: "PDF / resource",
      title: "Zero Trust buyer guide",
      path: "/resources/zero-trust-buyer-guide.pdf",
      lastReviewed: "2026-07-21",
      statuses: {
        assetCoverage: { status: "review", value: "Partial", detail: "Resource metadata is present, but document sections are summarized only." },
        entityClarity: { status: "review", value: "Needs review", detail: "Audience and product references need internal confirmation." },
        aiExtractability: { status: "blocked", value: "Gated", detail: "Document extraction is not run from the browser or fixture." },
        competitiveCompleteness: { status: "review", value: "Unknown", detail: "Competitive references are not represented in this demo asset." },
        freshnessTrust: { status: "review", value: "Stale watch", detail: "Older static review date should be checked before any real read." },
      },
    },
    {
      assetId: "asset-location-005",
      kind: "Local / location",
      title: "Boston solutions office",
      path: "/locations/boston",
      lastReviewed: "2026-07-26",
      statuses: {
        assetCoverage: { status: "review", value: "Partial", detail: "Location details are present without live local-search collection." },
        entityClarity: { status: "ready", value: "Clear", detail: "Location is associated with the organization and service area." },
        aiExtractability: { status: "blocked", value: "Gated", detail: "No AI call or live source collection is enabled." },
        competitiveCompleteness: { status: "review", value: "Partial", detail: "Local alternatives are intentionally not collected in the fixture." },
        freshnessTrust: { status: "review", value: "Watch", detail: "Static location details require a future server-side freshness check." },
      },
    },
  ],
  opportunityQueue: [
    {
      opportunityId: "opp-001",
      priority: "P0",
      opportunityType: "Extractability gap",
      affectedJourneyStage: "Evaluation",
      finding: "Product proof is strong but extractability is still gated.",
      affectedAsset: "Access control platform",
      recommendedInternalAction: "Review the product evidence mapping before approving a server-side assessment path.",
      recommendation: "Review the product evidence mapping before approving a server-side assessment path.",
      estimatedImpact: "High internal learning value",
      implementationEffort: "Medium",
      status: "review",
      safetyStatus: "Internal review only",
      safetyNote: "Internal review only; no AI call or customer output is created.",
    },
    {
      opportunityId: "opp-002",
      priority: "P1",
      opportunityType: "Entity clarity",
      affectedJourneyStage: "Consideration",
      finding: "The buyer guide has incomplete entity and competitive context.",
      affectedAsset: "Zero Trust buyer guide",
      recommendedInternalAction: "Confirm document audience, solution references, and competitive proof with the internal operator.",
      recommendation: "Confirm document audience, solution references, and competitive proof with the internal operator.",
      estimatedImpact: "Medium internal clarity gain",
      implementationEffort: "Low",
      status: "ready",
      safetyStatus: "Manual review ready",
      safetyNote: "Ready for manual internal review; PDF extraction remains disabled.",
    },
    {
      opportunityId: "opp-003",
      priority: "P1",
      opportunityType: "Freshness / trust",
      affectedJourneyStage: "Local discovery",
      finding: "Location coverage lacks a freshness/trust confirmation.",
      affectedAsset: "Boston solutions office",
      recommendedInternalAction: "Define a scoped server-side freshness check before relying on local asset signals.",
      recommendation: "Define a scoped server-side freshness check before relying on local asset signals.",
      estimatedImpact: "Medium trust protection",
      implementationEffort: "High",
      status: "blocked",
      safetyStatus: "Blocked for production",
      safetyNote: "Blocked until live collection and real-data approvals are separately gated.",
    },
  ],
};

/** Browser-safe route fixtures: static data only, with no Agent Smith or Supabase imports. */
const DEMO_READY_PREVIEW = mapValidatedInternalUiProjectionToBrowserSafeProps(STATIC_INTERNAL_UI_PROJECTION_FIXTURE);

function withScenario(
  base: CurrentAssetIntelligenceBrowserSafeProjectionProps,
  overrides: Partial<CurrentAssetIntelligenceBrowserSafeProjectionProps>
): CurrentAssetIntelligenceBrowserSafeProjectionProps {
  return { ...base, ...overrides };
}

export const CURRENT_ASSET_INTELLIGENCE_FIXTURES: Record<
  CurrentAssetIntelligenceBrowserSafeProjectionProps["scenario"],
  CurrentAssetIntelligenceBrowserSafeProjectionProps
> = {
  demoReady: DEMO_READY_PREVIEW,
  emptyAssets: withScenario(DEMO_READY_PREVIEW, {
    scenario: "emptyAssets",
    scenarioLabel: "No assets available yet",
    scenarioDetail: "The read model is valid, but no assets are available in this static scenario.",
    readoutSummary: {
      ...DEMO_READY_PREVIEW.readoutSummary,
      headline: "Awaiting asset inventory",
      detail: "No assets are available in this internal fixture scenario.",
      status: "review",
      scope: "Empty asset scenario",
    },
    previewSummary: { ...DEMO_READY_PREVIEW.previewSummary, totalAssetsReviewed: 0, assetsReadyForInternalReview: 0 },
    assetInventory: [],
    opportunityQueue: [],
  }),
  partialCoverage: withScenario(DEMO_READY_PREVIEW, {
    scenario: "partialCoverage",
    scenarioLabel: "Partial coverage warning",
    scenarioDetail: "Some static assets are present, but coverage gaps remain for internal review.",
    readoutSummary: {
      ...DEMO_READY_PREVIEW.readoutSummary,
      headline: "Partial asset coverage",
      detail: "The fixture includes a limited inventory and should not be treated as complete.",
      status: "review",
      scope: "Partial coverage scenario",
    },
    previewSummary: { ...DEMO_READY_PREVIEW.previewSummary, totalAssetsReviewed: 2, assetsReadyForInternalReview: 1 },
    assetInventory: DEMO_READY_PREVIEW.assetInventory.slice(0, 2),
    opportunityQueue: DEMO_READY_PREVIEW.opportunityQueue.slice(0, 1),
  }),
  blockedForRealData: withScenario(DEMO_READY_PREVIEW, {
    scenario: "blockedForRealData",
    scenarioLabel: "Blocked until server read approval",
    scenarioDetail: "This state is intentionally blocked until a scoped server-backed read is approved.",
    readoutSummary: {
      ...DEMO_READY_PREVIEW.readoutSummary,
      headline: "Server-backed read not approved",
      detail: "No real CAI data may appear until the server-side read approval path is complete.",
      status: "blocked",
      scope: "Blocked real-data scenario",
    },
    previewSummary: { ...DEMO_READY_PREVIEW.previewSummary, totalAssetsReviewed: 0, assetsReadyForInternalReview: 0 },
    assetInventory: [],
    opportunityQueue: [],
  }),
  localBusinessDemo: withScenario(DEMO_READY_PREVIEW, {
    scenario: "localBusinessDemo",
    profileLabel: "Local business demo profile",
    scenarioLabel: "Local profile preview",
    scenarioDetail: "Static local-business examples are shown for internal layout and status review.",
    accountRunContext: {
      ...DEMO_READY_PREVIEW.accountRunContext,
      accountLabel: "Harbor Street Dental · local demo",
      accountId: "acct_local_business_demo",
      runId: "run_local_business_browser_fixture",
    },
    previewSummary: { ...DEMO_READY_PREVIEW.previewSummary, totalAssetsReviewed: 2, assetsReadyForInternalReview: 1 },
    assetInventory: DEMO_READY_PREVIEW.assetInventory.filter((asset) => asset.kind === "Homepage" || asset.kind === "Local / location"),
  }),
  enterpriseDemo: withScenario(DEMO_READY_PREVIEW, {
    scenario: "enterpriseDemo",
    profileLabel: "Enterprise demo profile",
    scenarioLabel: "Enterprise profile preview",
    scenarioDetail: "Static enterprise examples are shown for internal layout and status review.",
    accountRunContext: {
      ...DEMO_READY_PREVIEW.accountRunContext,
      accountLabel: "Northstar Systems · enterprise demo",
      accountId: "acct_enterprise_demo",
      runId: "run_enterprise_browser_fixture",
    },
  }),
  torenVerifiedExistingSource: mapExistingSourceReadToBrowserSafeReadModel(torenVerifiedExistingSourceSnapshot),
  liveInternalStagingBlocked: withScenario(DEMO_READY_PREVIEW, {
    scenario: "liveInternalStagingBlocked",
    scenarioLabel: "Internal staging live read is not enabled",
    scenarioDetail: "Production remains disabled. Browser route is fixture-backed unless explicit staging approval is active. No browser Supabase reads or writes are performed.",
    readoutSummary: {
      ...DEMO_READY_PREVIEW.readoutSummary,
      headline: "Internal staging live read is not enabled",
      detail: "This blocked state is static route-gate data only; no network request was attempted.",
      status: "blocked",
      scope: "Staging gate blocked",
    },
    previewSummary: { ...DEMO_READY_PREVIEW.previewSummary, totalAssetsReviewed: 0, assetsReadyForInternalReview: 0 },
    assetInventory: [],
    opportunityQueue: [],
  }),
  liveInternalStaging: withScenario(DEMO_READY_PREVIEW, {
    scenario: "liveInternalStaging",
    profileLabel: "Local internal staging read",
    scenarioLabel: "Local internal staging read",
    scenarioDetail: "Blocked/unwired by default. A validated response may render only after explicit non-production staging approval.",
    accountRunContext: {
      ...DEMO_READY_PREVIEW.accountRunContext,
      accountLabel: "Toren · local internal staging",
      accountId: "72a5f4af-2997-4606-abe4-1e739b75d9c9",
      runId: "f0abacdf-d990-41ff-94c6-b0a2f0a19374",
    },
    readoutSummary: {
      ...DEMO_READY_PREVIEW.readoutSummary,
      headline: "Local internal staging read",
      detail: "Production disabled. Default route remains fixture-backed until a response is validated.",
      status: "blocked",
      scope: "Toren allowlisted staging scope",
    },
    previewSummary: { ...DEMO_READY_PREVIEW.previewSummary, totalAssetsReviewed: 0, assetsReadyForInternalReview: 0 },
    assetInventory: [],
    opportunityQueue: [],
  }),
};

const torenRehearsalQueue = BROWSER_SAFE_INTERNAL_REVIEW_PACK_FIXTURE.assetReviewQueue.map((item) => ({
  id: item.assetId,
  assetType: item.assetType,
  label: item.title,
  url: item.finalUrl,
  status: item.assessmentReadiness,
  reason: item.reviewReasonCodes[0] || "Bounded internal review item",
  priority: item.reviewPriority,
}));

/** Browser-safe Toren rehearsal result derived from the existing sanitized review fixture. */
export const TOREN_INTERNAL_OUTPUT_REHEARSAL_RESULT: InternalReviewPackResult = {
  status: "passed",
  reason: "toren_internal_output_rehearsal_fixture_ready",
  snapshotFound: true,
  reviewPackFound: true,
  customerOutputEnabled: false,
  browserSupabaseReads: 0,
  browserSupabaseWrites: 0,
  aiCalls: 0,
  rawPayloadReturned: false,
  noSecretReturned: true,
  reviewPack: {
    status: "passed",
    reason: "toren_internal_output_rehearsal_fixture_ready",
    snapshotFound: true,
    reviewPackFound: true,
    reviewPackVersion: "v1",
    rootDomain: "torenindex.com",
    accountId: "72a5f4af-2997-4606-abe4-1e739b75d9c9",
    runId: "f0abacdf-d990-41ff-94c6-b0a2f0a19374",
    createdAt: "2026-07-30T00:00:00.000Z",
    assessmentProfile: "internal_rehearsal",
    snapshotType: "internal_cai_snapshot",
    snapshotSchemaVersion: "v1",
    assetInventory: { totalAssets: torenRehearsalQueue.length, pageAssets: torenRehearsalQueue.length, detectedSourceTypes: ["html"], boundedAssetSample: torenRehearsalQueue.slice(0, 3) },
    sourceCoverage: { sitemapDetected: false, htmlSourcesDetected: true, metadataDetected: false, structuredDataDetected: false, internalEvidenceAvailable: true },
    operatorFindings: torenRehearsalQueue.map((item) => ({ id: `finding-${item.id}`, severity: item.priority, category: "asset_review", title: `${item.label} requires operator review`, evidenceSummary: item.reason, operatorAction: "Review the bounded internal evidence before any downstream decision.", confidence: "bounded" })),
    assetReviewQueue: torenRehearsalQueue,
    safetyPosture: { customerOutputEnabled: false, browserSupabaseReads: 0, browserSupabaseWrites: 0, writes: 0, aiCalls: 0, rawPayloadReturned: false, noSecretReturned: true },
  },
};

/** Default route fixture. Alternate scenarios remain known static fixtures only. */
export const CURRENT_ASSET_INTELLIGENCE_INTERNAL_PREVIEW_PROPS =
  CURRENT_ASSET_INTELLIGENCE_FIXTURES.demoReady;

/**
 * Optional internal-only selector. It can choose only a statically imported fixture;
 * it cannot trigger a read, fetch, network call, or server-side adapter.
 */
export function getCurrentAssetIntelligenceFixtureFromLocation(): CurrentAssetIntelligenceBrowserSafeProjectionProps {
  if (typeof window === "undefined") return CURRENT_ASSET_INTELLIGENCE_INTERNAL_PREVIEW_PROPS;
  const requested = new URLSearchParams(window.location.search).get("caiFixture");
  if (requested && Object.prototype.hasOwnProperty.call(CURRENT_ASSET_INTELLIGENCE_FIXTURES, requested)) {
    return CURRENT_ASSET_INTELLIGENCE_FIXTURES[requested as CurrentAssetIntelligenceBrowserSafeProjectionProps["scenario"]];
  }
  return CURRENT_ASSET_INTELLIGENCE_INTERNAL_PREVIEW_PROPS;
}
